<template>
  <iText
    v-bind="textOnlyAttributes"
    :width="myWidth"
    :height="myHeight"
    :style-overrides="separatorStyleOverrides"
  >
    {{ displayLabel }}
  </iText>
</template>

<script>
import TextElement
  from "%/dynamic-component-editor/standard-elements/text-element/TextElement.vue";
import BrandedTextStyleOptions from "%/dynamic-component-editor/bundles/branded-text-style-options";
import EffectsOptions from "%/dynamic-component-editor/bundles/effects-options";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import { feedSeparatorLabelContextName } from "%/dynamic-component-editor/helpers/feed-separator-label";

// Renders the label of the feed separator it sits in ("TODAY", "THIS WEEK"). The
// value comes from the FEED, not from a row entity, so it arrives as a scoped
// VALUE context under the name the component declared -- not through
// valueFromContext, which reads an entity FIELD and has nowhere to put a loose
// string.
//
// Styling is inherited wholesale from TextElement, so an author styles a section
// separator with the same controls as any other text.
export default {
  name: "FeedSeparatorLabel",
  elementPanelSettings: {
    displayName: "Feed Separator Label",
    displayIcon: "text-alt",
    elementType: "element",
    elementGroup: "general",
  },
  extends: TextElement,
  inject: {
    scopedProvidedContexts: { default: null },
  },
  optionBundles: [
    new BrandedTextStyleOptions({}, {
      "text-option": true,
    }),
    new EffectsOptions({}, {
      "text-shadow": true,
      "box-shadow": true,
    }),
    // A separator is a band BETWEEN rows, so it needs vertical room the moment
    // it is dropped -- with no padding it collapses to its line height and sits
    // flush against the cards on either side. Horizontal padding stays off so
    // the label lines up with the feed's own left edge.
    new SpacingOptions({
      "default.padding": {
        "top": "standard",
        "right": "none",
        "bottom": "standard",
        "left": "none",
      },
    }),
  ],
  options: [
    new LayoutHeight({ "default": "hug" }, true),
    new LayoutWidth({ "default": "fill" }, true),
  ],
  computed: {
    // `textOnlyAttributes` is the FONT-ONLY subset -- it deliberately omits
    // spacing so a button's label cannot fight the button's own padding. This
    // element IS its own box, so it declares SpacingOptions and has to put the
    // spacing back, or an author's padding persists and never renders.
    separatorStyleOverrides() {
      return {
        ...this.textOnlyAttributes.textStyleOverrides,
        ...this.spacingStyleOverrides,
      };
    },
    // The feed supplies the label under the name the component declared, so read
    // whichever scoped context the declaration named rather than a fixed key.
    providedLabel() {
      const scope = typeof this.scopedProvidedContexts === "function"
        ? this.scopedProvidedContexts()
        : this.scopedProvidedContexts;
      const contextName = feedSeparatorLabelContextName(this.dynamicComponentInstance);
      const value = contextName ? scope?.[contextName] : null;
      return typeof value === "string" ? value : "";
    },
    // On the canvas there is no feed above this element, so show a sample rather
    // than an empty box the author cannot see to style.
    displayLabel() {
      if (this.providedLabel) {
        return this.providedLabel;
      }
      return this.editMode || this.previewMode ? "Feed separator label" : "";
    },
  },
};
</script>
